import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Lightbulb, Loader2, ExternalLink, Image, Video, Eye, Zap, X, Calendar, Save, Upload, FileDown, GripVertical } from 'lucide-react';

const TipReactionCounts = ({ data, tipId }) => {
  const entry = data?.counts?.[tipId];
  if (!entry) return null;
  return (
    <div className="flex flex-wrap gap-1 mb-3" data-testid={`tip-reaction-counts-${tipId}`}>
      {Object.entries(entry.reactions).sort((a, b) => b[1] - a[1]).map(([key, n]) => (
        <span key={key} title={data.meta[key]?.label} className="text-[11px] bg-stone-100 text-stone-700 rounded-full px-1.5 py-0.5">
          {data.meta[key]?.emoji} {n}
        </span>
      ))}
    </div>
  );
};

const AdminTips = () => {
  const [reactionData, setReactionData] = useState(null);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeSet, setActiveSet] = useState('1');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTip, setPreviewTip] = useState(null);
  
  // Image upload state
  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef(null);
  
  // PDF upload state
  const [pdfUploading, setPdfUploading] = useState(false);
  const pdfInputRef = useRef(null);
  
  // Off-day tip settings state
  const [offDaySettings, setOffDaySettings] = useState({
    title: '',
    content: '',
    gif_url: ''
  });
  const [offDaySaving, setOffDaySaving] = useState(false);
  const [offDayPreviewOpen, setOffDayPreviewOpen] = useState(false);
  const [offDayUploading, setOffDayUploading] = useState(false);
  const offDayImageInputRef = useRef(null);
  const [fixingUrls, setFixingUrls] = useState(false);

  // Drag-and-drop state for reordering tips within a set.
  // We mirror the drag info in a ref so the drop handler isn't blocked by React's
  // asynchronous state updates (stale closure problem on fast drops).
  const [dragging, setDragging] = useState(null); // { tipId, fromDay, setNum }
  const draggingRef = useRef(null);
  const [dragOverDay, setDragOverDay] = useState(null); // dayNum being hovered
  const [reordering, setReordering] = useState(false);
  
  const [form, setForm] = useState({
    title: '',
    content: '',
    gif_keywords: 'motivation fitness',
    gif_url: '',
    external_link: '',
    image_url: '',
    video_url: '',
    pdf_url: '',
    day_number: 1,
    tip_set: 1
  });

  useEffect(() => {
    fetchTips();
    fetchOffDaySettings();
    apiClient.get('/admin/tips/reaction-counts').then((res) => setReactionData(res.data)).catch(() => {});
  }, []);

  const fetchTips = async () => {
    try {
      const res = await apiClient.get('/tips');
      setTips(res.data);
    } catch (err) {
      toast.error('Failed to fetch tips');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchOffDaySettings = async () => {
    try {
      const res = await apiClient.get('/admin/off-day-tip');
      setOffDaySettings(res.data);
    } catch (err) {
      console.error('Failed to fetch off-day settings:', err);
    }
  };
  
  const saveOffDaySettings = async () => {
    if (!offDaySettings.title || !offDaySettings.content) {
      toast.error('Please fill in title and content');
      return;
    }
    
    setOffDaySaving(true);
    try {
      await apiClient.put('/admin/off-day-tip', offDaySettings);
      toast.success('Off-day tip settings saved!');
    } catch (err) {
      toast.error('Failed to save off-day settings');
    } finally {
      setOffDaySaving(false);
    }
  };

  // Fix old image URLs (one-time migration)
  const fixImageUrls = async () => {
    setFixingUrls(true);
    try {
      const res = await apiClient.post('/admin/fix-image-urls');
      if (res.data.fixed_count > 0) {
        toast.success(`Fixed ${res.data.fixed_count} image URL(s)! Refreshing...`);
        await fetchTips(); // Refresh tips to get updated URLs
      } else {
        toast.info('All image URLs are already correct!');
      }
    } catch (err) {
      toast.error('Failed to fix image URLs');
    } finally {
      setFixingUrls(false);
    }
  };

  // Image upload handler for daily tips
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WebP');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await apiClient.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Construct full URL
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const fullUrl = `${baseUrl}${res.data.url}`;
      
      setForm(prev => ({ ...prev, image_url: fullUrl }));
      toast.success('Image uploaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Reset the file input
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  // PDF upload handler for tips
  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error('Invalid file type. Please upload a PDF file');
      return;
    }
    
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    
    setPdfUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await apiClient.post('/upload/pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Construct full URL
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const fullUrl = `${baseUrl}${res.data.url}`;
      
      setForm(prev => ({ ...prev, pdf_url: fullUrl }));
      toast.success('PDF uploaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setPdfUploading(false);
      if (pdfInputRef.current) {
        pdfInputRef.current.value = '';
      }
    }
  };

  // Image upload handler for off-day tip
  const handleOffDayImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WebP');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setOffDayUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await apiClient.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const fullUrl = `${baseUrl}${res.data.url}`;
      
      setOffDaySettings(prev => ({ ...prev, gif_url: fullUrl }));
      toast.success('Image uploaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setOffDayUploading(false);
      if (offDayImageInputRef.current) {
        offDayImageInputRef.current.value = '';
      }
    }
  };

  const resetForm = () => {
    const currentSetTips = tips.filter(t => t.tip_set === parseInt(activeSet));
    const usedDays = currentSetTips.map(t => t.day_number);
    let nextDay = 1;
    while (usedDays.includes(nextDay) && nextDay <= 28) nextDay++;
    
    setForm({
      title: '',
      content: '',
      gif_keywords: 'motivation fitness',
      gif_url: '',
      external_link: '',
      image_url: '',
      video_url: '',
      pdf_url: '',
      day_number: nextDay,
      tip_set: parseInt(activeSet)
    });
    setEditing(null);
  };

  const openEdit = (tip) => {
    setForm({
      title: tip.title,
      content: tip.content,
      gif_keywords: tip.gif_keywords || 'motivation',
      gif_url: tip.gif_url || '',
      external_link: tip.external_link || '',
      image_url: tip.image_url || '',
      video_url: tip.video_url || '',
      pdf_url: tip.pdf_url || '',
      day_number: tip.day_number,
      tip_set: tip.tip_set
    });
    setEditing(tip.id);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) {
      toast.error('Please fill in title and content');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await apiClient.put(`/tips/${editing}`, form);
        toast.success('Tip updated');
      } else {
        await apiClient.post('/tips', form);
        toast.success('Tip created');
      }
      fetchTips();
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save tip');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tip?')) return;
    
    try {
      await apiClient.delete(`/tips/${id}`);
      toast.success('Tip deleted');
      fetchTips();
    } catch (err) {
      toast.error('Failed to delete tip');
    }
  };

  const openPreview = (tip) => {
    // No auto-generated GIF - show exactly what's in the tip
    setPreviewTip(tip);
    setPreviewOpen(true);
  };

  const openFormPreview = () => {
    if (!form.title || !form.content) {
      toast.error('Please fill in title and content to preview');
      return;
    }
    
    // No auto-generated GIF - preview shows exactly what admin entered
    setPreviewTip({
      ...form,
      id: 'preview'
    });
    setPreviewOpen(true);
  };

  const getTipsForSet = (setNum) => {
    return tips.filter(t => Number(t.tip_set) === Number(setNum));
  };

  const handleTipDragStart = (e, tip) => {
    const info = { tipId: tip.id, fromDay: tip.day_number, setNum: Number(tip.tip_set) };
    draggingRef.current = info;
    setDragging(info);
    try {
      e.dataTransfer.effectAllowed = 'move';
      // Firefox / some Chromium builds require setData() for the drag to actually start.
      e.dataTransfer.setData('text/plain', tip.id);
    } catch (_err) { /* no-op */ }
  };

  const handleDayDragOver = (e, dayNum, setNum) => {
    const drag = draggingRef.current;
    if (!drag || drag.setNum !== Number(setNum)) return;
    // MUST preventDefault on every dragover to keep the target a valid drop zone.
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverDay !== dayNum) setDragOverDay(dayNum);
  };

  const handleDayDragLeave = () => {
    setDragOverDay(null);
  };

  const persistReorder = async (setNum, assignments) => {
    setReordering(true);
    try {
      await apiClient.post('/admin/tips/reorder', { tip_set: setNum, assignments });
      await fetchTips();
      toast.success('Order updated');
    } catch (err) {
      toast.error('Failed to reorder — reverting');
      await fetchTips();
    } finally {
      setReordering(false);
    }
  };

  const handleDayDrop = (e, targetDay, setNum) => {
    e.preventDefault();
    const drag = draggingRef.current;
    draggingRef.current = null;
    setDragging(null);
    setDragOverDay(null);
    if (!drag || drag.setNum !== Number(setNum)) return;
    if (drag.fromDay === targetDay) return;

    const currentSetTips = getTipsForSet(setNum);
    const source = currentSetTips.find(t => t.id === drag.tipId);
    const target = currentSetTips.find(t => t.day_number === targetDay);
    if (!source) return;

    // Optimistic local update
    setTips(prev => prev.map(t => {
      if (t.id === source.id) return { ...t, day_number: targetDay };
      if (target && t.id === target.id) return { ...t, day_number: drag.fromDay };
      return t;
    }));

    const assignments = [{ tip_id: source.id, day_number: targetDay }];
    if (target) assignments.push({ tip_id: target.id, day_number: drag.fromDay });
    persistReorder(Number(setNum), assignments);
  };

  const renderTipGrid = (setNum) => {
    const setTips = getTipsForSet(setNum);
    
    return (
      <>
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-stone-500 flex items-center gap-1.5">
            <GripVertical size={12} className="text-stone-400" />
            Drag any tip onto another day to swap or move it.
          </p>
          {reordering && (
            <span className="text-xs text-[#B8871E] flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" />
              Saving new order…
            </span>
          )}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(28)].map((_, i) => {
          const dayNum = i + 1;
          const tip = setTips.find(t => t.day_number === dayNum);
          
          return (
            <div
              key={dayNum}
              className={`rounded-2xl border p-4 transition-colors ${
                tip 
                  ? 'bg-white border-stone-200 hover:border-stone-300 cursor-grab active:cursor-grabbing' 
                  : 'bg-stone-50/30 border-stone-200/50 border-dashed'
              } ${dragOverDay === dayNum && dragging?.setNum === Number(setNum) ? 'ring-2 ring-[#EFB342] bg-[#FFF8E7]' : ''} ${dragging?.tipId === tip?.id ? 'opacity-40' : ''}`}
              draggable={!!tip}
              onDragStart={tip ? (e) => handleTipDragStart(e, tip) : undefined}
              onDragEnd={() => { draggingRef.current = null; setDragging(null); setDragOverDay(null); }}
              onDragOver={(e) => handleDayDragOver(e, dayNum, setNum)}
              onDragLeave={handleDayDragLeave}
              onDrop={(e) => handleDayDrop(e, dayNum, setNum)}
              data-testid={`tip-set${setNum}-day-${dayNum}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-1 ${
                  tip ? 'text-[#EFB342]' : 'text-stone-400'
                }`}>
                  {tip && (
                    <GripVertical size={12} className="text-stone-400 cursor-grab active:cursor-grabbing" />
                  )}
                  Day {dayNum}
                </span>
                {tip && (
                  <div className="flex items-center gap-1">
                    {tip.external_link && <ExternalLink size={12} className="text-stone-500" />}
                    {tip.image_url && <Image size={12} className="text-stone-500" />}
                    {tip.video_url && <Video size={12} className="text-stone-500" />}
                  </div>
                )}
              </div>
              
              {tip ? (
                <>
                  <h3 className="font-semibold text-stone-900 text-sm mb-1 line-clamp-1">{tip.title}</h3>
                  <p className="text-stone-600 text-xs line-clamp-2 mb-3">{tip.content}</p>
                  <TipReactionCounts data={reactionData} tipId={tip.id} />
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openPreview(tip)}
                      className="text-[#EFB342] hover:text-[#D99F30] h-7 px-2"
                      data-testid={`preview-tip-${tip.id}`}
                    >
                      <Eye size={14} className="mr-1" />
                      Preview
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(tip)}
                      className="text-stone-600 hover:text-stone-900 h-7 px-2"
                      data-testid={`edit-tip-${tip.id}`}
                    >
                      <Pencil size={14} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(tip.id)}
                      className="text-stone-600 hover:text-red-500 h-7 px-2"
                      data-testid={`delete-tip-${tip.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </>
              ) : (
                <button
                  onClick={() => {
                    setForm({ ...form, day_number: dayNum, tip_set: setNum });
                    setDialogOpen(true);
                  }}
                  className="w-full py-4 flex flex-col items-center justify-center text-stone-400 hover:text-stone-600 transition-colors"
                >
                  <Lightbulb size={24} className="mb-2" />
                  <span className="text-xs">Add tip</span>
                </button>
              )}
            </div>
          );
        })}
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900">DAILY TIPS</h1>
            <p className="text-stone-600 text-sm">Create 4 sets of 28 tips to rotate monthly</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={fixImageUrls} 
              disabled={fixingUrls}
              className="border-stone-300 text-stone-700 hover:bg-stone-100"
              data-testid="fix-urls-btn"
            >
              {fixingUrls ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Image size={16} className="mr-2" />}
              Fix Image URLs
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold" data-testid="add-tip-btn">
                  <Plus size={18} className="mr-2" />
                  Add Tip
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-white border-stone-200 text-stone-900 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">
                  {editing ? 'Edit Tip' : 'Create Tip'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Day Number *</Label>
                    <Input
                      type="number"
                      min="1"
                      max="28"
                      value={form.day_number}
                      onChange={(e) => setForm({ ...form, day_number: parseInt(e.target.value) || 1 })}
                      className="bg-stone-50 border-stone-200 mt-1"
                      data-testid="tip-day-input"
                    />
                  </div>
                  <div>
                    <Label>Tip Set *</Label>
                    <Select value={form.tip_set.toString()} onValueChange={(v) => setForm({ ...form, tip_set: parseInt(v) })}>
                      <SelectTrigger className="bg-stone-50 border-stone-200 mt-1" data-testid="tip-set-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-stone-200">
                        <SelectItem value="1">Set 1</SelectItem>
                        <SelectItem value="2">Set 2</SelectItem>
                        <SelectItem value="3">Set 3</SelectItem>
                        <SelectItem value="4">Set 4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Hydration Station"
                    className="bg-stone-50 border-stone-200 mt-1"
                    data-testid="tip-title-input"
                  />
                </div>
                <div>
                  <Label>Content *</Label>
                  <Textarea
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    placeholder="Your body is 60% water. Start your day with a full glass before coffee!"
                    className="bg-stone-50 border-stone-200 mt-1"
                    rows={3}
                    data-testid="tip-content-input"
                  />
                </div>
                <div>
                  <Label>GIF Keywords (auto-generate if no GIF URL)</Label>
                  <Input
                    value={form.gif_keywords}
                    onChange={(e) => setForm({ ...form, gif_keywords: e.target.value })}
                    placeholder="water drink hydration"
                    className="bg-stone-50 border-stone-200 mt-1"
                    data-testid="tip-keywords-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">Used only if GIF URL is empty</p>
                </div>
                <div>
                  <Label>GIF URL (paste your own GIF)</Label>
                  <Input
                    value={form.gif_url}
                    onChange={(e) => setForm({ ...form, gif_url: e.target.value })}
                    placeholder="https://media.giphy.com/media/xxxxx/giphy.gif"
                    className="bg-stone-50 border-stone-200 mt-1"
                    data-testid="tip-gif-url-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">From Giphy, Tenor, or any .gif link</p>
                </div>
                <div>
                  <Label>External Link (optional)</Label>
                  <Input
                    value={form.external_link}
                    onChange={(e) => setForm({ ...form, external_link: e.target.value })}
                    placeholder="https://buildnutrition.ca/blog/article"
                    className="bg-stone-50 border-stone-200 mt-1"
                    data-testid="tip-link-input"
                  />
                </div>
                <div>
                  <Label>Image URL (optional)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={form.image_url}
                      onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                      className="bg-stone-50 border-stone-200 flex-1"
                      data-testid="tip-image-input"
                    />
                    <input
                      type="file"
                      ref={imageInputRef}
                      onChange={handleImageUpload}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploading}
                      className="border-stone-300 text-stone-700 hover:bg-stone-100"
                    >
                      {uploading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Upload size={16} />
                      )}
                    </Button>
                  </div>
                  {form.image_url && (
                    <div className="mt-2 p-2 bg-stone-50 rounded-lg border border-stone-200">
                      <img 
                        src={form.image_url} 
                        alt="Preview" 
                        className="h-20 object-contain rounded"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <Label>Video URL (optional)</Label>
                  <Input
                    value={form.video_url}
                    onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                    className="bg-stone-50 border-stone-200 mt-1"
                    data-testid="tip-video-input"
                  />
                </div>
                <div>
                  <Label>PDF Attachment (optional - for recipes, guides, etc.)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={form.pdf_url}
                      onChange={(e) => setForm({ ...form, pdf_url: e.target.value })}
                      placeholder="https://example.com/recipe.pdf"
                      className="bg-stone-50 border-stone-200 flex-1"
                      data-testid="tip-pdf-input"
                    />
                    <input
                      type="file"
                      ref={pdfInputRef}
                      onChange={handlePdfUpload}
                      accept="application/pdf"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => pdfInputRef.current?.click()}
                      disabled={pdfUploading}
                      className="border-stone-300 text-stone-700 hover:bg-stone-100"
                    >
                      {pdfUploading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Upload size={16} />
                      )}
                    </Button>
                  </div>
                  {form.pdf_url && (
                    <div className="mt-2 p-2 bg-stone-50 rounded-lg border border-stone-200 flex items-center gap-2">
                      <span className="text-xs text-green-400">✓ PDF attached</span>
                      <a 
                        href={form.pdf_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-[#EFB342] hover:underline"
                      >
                        Preview PDF
                      </a>
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={openFormPreview}
                    className="flex-1 border-[#EFB342] text-[#EFB342] hover:bg-[#EFB342]/10"
                    data-testid="tip-preview-btn"
                  >
                    <Eye size={16} className="mr-2" />
                    Preview
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold"
                    data-testid="tip-submit-btn"
                  >
                    {saving ? <Loader2 className="animate-spin" size={18} /> : editing ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Preview Modal */}
        {previewOpen && previewTip && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#F5F4F1]/80" onClick={() => setPreviewOpen(false)}>
            <div className="relative w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
              {/* Close button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPreviewOpen(false)}
                className="absolute -top-12 right-0 text-stone-900 hover:bg-stone-100"
              >
                <X size={20} className="mr-1" />
                Close Preview
              </Button>
              
              {/* Preview label */}
              <div className="text-center mb-3">
                <span className="bg-[#EFB342] text-black text-xs font-bold px-3 py-1 rounded-full">
                  USER VIEW PREVIEW
                </span>
              </div>
              
              {/* Tip card preview - matches Dashboard.jsx styling */}
              <div className="bg-white/95 border border-stone-200 rounded-2xl overflow-hidden shadow-2xl">
                <div className="p-4 md:p-6">
                  <div className="flex items-center justify-between mb-3 md:mb-4">
                    <div className="flex items-center gap-2">
                      <Zap className="text-[#EFB342]" size={18} />
                      <span className="text-xs text-stone-500 uppercase tracking-wider">Daily GRIT Tip</span>
                    </div>
                  </div>
                  
                  <h3 className="font-heading text-lg md:text-xl font-bold text-stone-900 mb-2 md:mb-3">{previewTip.title}</h3>
                  <p className="text-stone-700 text-sm leading-relaxed mb-3 md:mb-4 whitespace-pre-wrap">{previewTip.content}</p>
                  
                  {/* PDF Download Button */}
                  {previewTip.pdf_url && (
                    <a 
                      href={previewTip.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#EFB342]/20 text-[#EFB342] hover:bg-[#EFB342]/30 px-4 py-2 rounded-lg text-sm font-medium transition-colors mb-3"
                    >
                      <FileDown size={16} />
                      Download Recipe/Guide (PDF)
                    </a>
                  )}
                  
                  {previewTip.external_link && (
                    <a 
                      href={previewTip.external_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#EFB342] hover:underline text-sm"
                    >
                      Learn more <ExternalLink size={14} />
                    </a>
                  )}
                </div>
                
                {/* Priority: image_url > gif_url > fallback GIF */}
                {previewTip.image_url && (
                  <div className="border-t border-stone-200 bg-[#F5F4F1] flex items-center justify-center">
                    <img 
                      src={previewTip.image_url} 
                      alt="Tip image"
                      className="w-full max-h-64 md:max-h-80 object-contain"
                    />
                  </div>
                )}
                
                {previewTip.gif_url && !previewTip.image_url && (
                  <div className="border-t border-stone-200 bg-[#F5F4F1] flex items-center justify-center">
                    <img 
                      src={previewTip.gif_url} 
                      alt="Daily motivation"
                      className="w-full max-h-64 md:max-h-80 object-contain"
                    />
                  </div>
                )}
              </div>
              
              {/* Mobile frame indicator */}
              <p className="text-center text-stone-500 text-xs mt-3">
                This is how users will see this tip on their dashboard
              </p>
            </div>
          </div>
        )}

        {/* Info Banner */}
        <div className="bg-white border border-stone-200 rounded-xl p-4 mb-6">
          <p className="text-stone-600 text-sm">
            <strong className="text-stone-900">Tip Sets:</strong> Create 4 different sets of daily tips to provide variety each month. 
            Assign a tip set to each monthly challenge to rotate content automatically.
          </p>
        </div>

        {/* Tabs for Tip Sets */}
        <Tabs value={activeSet} onValueChange={setActiveSet} className="w-full">
          <TabsList className="bg-white border border-stone-200 mb-6">
            <TabsTrigger value="1" className="data-[state=active]:bg-[#EFB342] data-[state=active]:text-black">
              Set 1 ({getTipsForSet(1).length}/28)
            </TabsTrigger>
            <TabsTrigger value="2" className="data-[state=active]:bg-[#EFB342] data-[state=active]:text-black">
              Set 2 ({getTipsForSet(2).length}/28)
            </TabsTrigger>
            <TabsTrigger value="3" className="data-[state=active]:bg-[#EFB342] data-[state=active]:text-black">
              Set 3 ({getTipsForSet(3).length}/28)
            </TabsTrigger>
            <TabsTrigger value="4" className="data-[state=active]:bg-[#EFB342] data-[state=active]:text-black">
              Set 4 ({getTipsForSet(4).length}/28)
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="1">
            {renderTipGrid(1)}
          </TabsContent>
          <TabsContent value="2">
            {renderTipGrid(2)}
          </TabsContent>
          <TabsContent value="3">
            {renderTipGrid(3)}
          </TabsContent>
          <TabsContent value="4">
            {renderTipGrid(4)}
          </TabsContent>
        </Tabs>

        {/* Off-Day Settings Section - At bottom for better workflow */}
        <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/30 rounded-xl p-4 md:p-6 mt-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Calendar className="text-purple-400" size={20} />
            </div>
            <div>
              <h2 className="font-heading text-lg font-bold text-stone-900">Off-Day Tip (Days 29-31)</h2>
              <p className="text-stone-600 text-sm">Customize the message shown between monthly challenges</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <Label className="text-stone-700">Title</Label>
                <Input
                  value={offDaySettings.title}
                  onChange={(e) => setOffDaySettings({ ...offDaySettings, title: e.target.value })}
                  placeholder="🎉 Challenge Break!"
                  className="bg-stone-50 border-stone-300 mt-1"
                  data-testid="offday-title-input"
                />
              </div>
              <div>
                <Label className="text-stone-700">Content</Label>
                <Textarea
                  value={offDaySettings.content}
                  onChange={(e) => setOffDaySettings({ ...offDaySettings, content: e.target.value })}
                  placeholder="Next challenge starts on {next_month} 1st!"
                  className="bg-stone-50 border-stone-300 mt-1"
                  rows={3}
                  data-testid="offday-content-input"
                />
                <p className="text-xs text-stone-500 mt-1">Use <code className="bg-stone-100 px-1 rounded">{'{next_month}'}</code> to auto-insert the next month name</p>
              </div>
              <div>
                <Label className="text-stone-700">GIF/Image URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={offDaySettings.gif_url}
                    onChange={(e) => setOffDaySettings({ ...offDaySettings, gif_url: e.target.value })}
                    placeholder="https://media.giphy.com/media/xxxxx/giphy.gif"
                    className="bg-stone-50 border-stone-300 flex-1"
                    data-testid="offday-gif-input"
                  />
                  <input
                    type="file"
                    ref={offDayImageInputRef}
                    onChange={handleOffDayImageUpload}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => offDayImageInputRef.current?.click()}
                    disabled={offDayUploading}
                    className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
                  >
                    {offDayUploading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Upload size={16} />
                    )}
                  </Button>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setOffDayPreviewOpen(true)}
                  className="flex-1 border-purple-500 text-purple-400 hover:bg-purple-500/10"
                  data-testid="offday-preview-btn"
                >
                  <Eye size={16} className="mr-2" />
                  Preview
                </Button>
                <Button
                  onClick={saveOffDaySettings}
                  disabled={offDaySaving}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-stone-900 font-bold"
                  data-testid="offday-save-btn"
                >
                  {offDaySaving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={16} className="mr-2" />Save</>}
                </Button>
              </div>
            </div>
            
            {/* Preview thumbnail */}
            <div className="hidden md:block">
              {offDaySettings.gif_url && (
                <div className="bg-stone-50 rounded-xl overflow-hidden border border-stone-200">
                  <div className="p-3 border-b border-stone-200">
                    <span className="text-xs text-stone-500 uppercase tracking-wider">GIF Preview</span>
                  </div>
                  <img 
                    src={offDaySettings.gif_url} 
                    alt="Off-day GIF preview" 
                    className="w-full h-48 object-contain bg-[#F5F4F1]"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
              {!offDaySettings.gif_url && (
                <div className="bg-stone-50 rounded-xl border border-stone-200 h-48 flex items-center justify-center">
                  <p className="text-stone-400 text-sm">Add a GIF URL to preview</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Off-Day Preview Modal */}
        {offDayPreviewOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#F5F4F1]/80" onClick={() => setOffDayPreviewOpen(false)}>
            <div className="relative w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOffDayPreviewOpen(false)}
                className="absolute -top-12 right-0 text-stone-900 hover:bg-stone-100"
              >
                <X size={20} className="mr-1" />
                Close Preview
              </Button>
              
              <div className="text-center mb-3">
                <span className="bg-purple-500 text-stone-900 text-xs font-bold px-3 py-1 rounded-full">
                  OFF-DAY PREVIEW (Days 29-31)
                </span>
              </div>
              
              <div className="bg-white/95 border border-stone-200 rounded-2xl overflow-hidden shadow-2xl">
                <div className="p-4 md:p-6">
                  <div className="flex items-center justify-between mb-3 md:mb-4">
                    <div className="flex items-center gap-2">
                      <Zap className="text-[#EFB342]" size={18} />
                      <span className="text-xs text-stone-500 uppercase tracking-wider">Today's GRIT Intel</span>
                    </div>
                  </div>
                  
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-stone-900 mb-2 md:mb-3">
                    {offDaySettings.title || '🎉 Challenge Break!'}
                  </h3>
                  <p className="text-stone-700 text-sm md:text-base leading-relaxed mb-3 md:mb-4 whitespace-pre-wrap">
                    {(offDaySettings.content || 'Next challenge starts on {next_month} 1st!').replace('{next_month}', 'February')}
                  </p>
                </div>
                
                {offDaySettings.gif_url && (
                  <div className="border-t border-stone-200 bg-[#F5F4F1] flex items-center justify-center">
                    <img 
                      src={offDaySettings.gif_url} 
                      alt="Off-day motivation"
                      className="w-full max-h-64 md:max-h-80 object-contain"
                    />
                  </div>
                )}
              </div>
              
              <p className="text-center text-stone-500 text-xs mt-3">
                This is shown to users on days 29, 30, and 31
              </p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminTips;
